import React from 'react';
import {
    FilterListConfigFilterCellInterface,
    FilterListConfigPagerInterface,
    SelectOption,
} from '~types/FilterListConfigInterface';
import DateSelect from '../../../atoms/DateSelect';
import { getFormattedDate } from '~utils/date';
import { BasicObject } from '~components/ObjectHelper';
import MonthSelect from '../../../atoms/MonthSelect';
import { getLocaleFromHtml } from '~utils/locale';
import FilterTextInput from '~react/components/FilterList/components/FilterTextInput';
import PerPageSelect from '~react/components/FilterList/components/PerPageSelect';
import Select, { NonceProvider } from 'react-select';

interface FilterProps {
    cells: FilterListConfigFilterCellInterface[];
    values: BasicObject;
    onChange: (name: string, value: string, reload: boolean) => void;
    onPerPageChange: (perPage: number) => void;
    pager: FilterListConfigPagerInterface | null;
    cssNonce: string;
}

const Filter: React.FC<FilterProps> = props => {
    const getReload = (value: string, config: string): boolean => {
        return !(
            ('' !== value && 'ON_EMPTY' === config) ||
            ('' === value && 'ON_NOT_NULL' === config) ||
            'NONE' === config
        );
    };
    const filterInputs = props.cells.map((input, index) => {
        let inputElement = <></>;

        switch (input.type) {
            case 'text':
                inputElement = (
                    <FilterTextInput
                        key={input.name + index}
                        id={input.name}
                        defaultValue={props.values[input.name]}
                        onChange={value => {
                            props.onChange(input.name, value, false);
                        }}
                    />
                );
                break;
            case 'select':
                inputElement = (
                    <div className="custom-select" key={input.name + index}>
                        <select
                            value={props.values[input.name]}
                            onChange={event => {
                                props.onChange(
                                    input.name,
                                    event.currentTarget.value,
                                    getReload(event.currentTarget.value, input.reloadActionType),
                                );
                            }}
                        >
                            {input.options.map(({ value, label }) => (
                                <option value={value} key={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                break;
            case 'multiselect':
                inputElement = (
                    <NonceProvider nonce={props.cssNonce} cacheKey={'grid'}>
                        <Select
                            classNamePrefix="multiselect"
                            defaultValue={(props.values[input.name] ?? '')
                                .replace(/(%2C)/g, ',')
                                .split(',')
                                .map((value: string) => input.options.find(option => option.value === value))
                                .filter((option: unknown) => option !== undefined)}
                            isClearable={false}
                            name={input.name}
                            options={input.options}
                            isMulti={true}
                            autosize={false}
                            onChange={(value: Array<SelectOption>) => {
                                props.onChange(input.name, value.map(option => option.value).join(','), false);
                            }}
                            defaultMenuIsOpen={false}
                            placeholder={''}
                        />
                    </NonceProvider>
                );
                break;
            case 'date':
                return (
                    <div className="filter-date-select" key={input.name + index}>
                        <DateSelect
                            value={props.values[input.name]}
                            name={input.name}
                            id={input.name}
                            label={input.label}
                            onDateChange={date => {
                                props.onChange(input.name, date !== null ? getFormattedDate(date) : '', false);
                            }}
                            container={true}
                            texts={{ calendar: input.label }}
                            locale={getLocaleFromHtml()}
                        />
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="form__cell" key={input.name + index}>
                        <div className="checkbox__cell">
                            <input
                                type="checkbox"
                                id={input.name}
                                name={input.name}
                                checked={'1' === props.values[input.name]}
                                onChange={event => {
                                    props.onChange(
                                        input.name,
                                        (event.currentTarget as HTMLInputElement).checked ? '1' : '0',
                                        false,
                                    );
                                }}
                            />
                            <label htmlFor={input.name} className="form__label">
                                {input.label}
                            </label>
                        </div>
                    </div>
                );
            case 'months':
                return (
                    <MonthSelect
                        key={input.name + index}
                        value={new Date(Date.parse(props.values[input.name]))}
                        label={input.label}
                        id={input.name}
                        startDate={new Date(Date.parse(input.from))}
                        endDate={new Date(Date.parse(input.to))}
                        onChange={date => {
                            props.onChange(input.name, date, false);
                        }}
                    />
                );
        }

        return (
            <div className="form__cell" key={input.name}>
                <label className="form__label" htmlFor={input.name}>
                    {input.label}
                </label>
                {inputElement}
            </div>
        );
    });

    return 0 === props.cells.length ? null : (
        <div className="filter -rounded">
            {filterInputs}
            {props.pager ? (
                <PerPageSelect
                    perPageOptions={props.pager.perPageOptions}
                    label={props.pager.perPageLabel}
                    value={props.values.perPage}
                    onChange={props.onPerPageChange}
                />
            ) : (
                ''
            )}
        </div>
    );
};

export default Filter;
